/**
 * WidgetApi — тонкая обёртка над `fetch` для трёх публичных widget-эндпоинтов.
 * Не хранит состояние — инстанцируется на каждый Widget. Параметр
 * `fetchImpl` нужен для тестов; production-код использует
 * глобальный `fetch`.
 *
 * @typedef {Object} SearchResponse
 * @property {string} query_id
 * @property {Array<{id: string, score: number, source: Record<string, unknown>}>} hits
 * @property {number} total
 * @property {number} took_ms
 * @property {string} mode
 * @property {boolean} degraded
 *
 * @typedef {Object} SuggestResponse
 * @property {Array<{text: string, score: number, product_id?: string}>} items
 */

export class ApiError extends Error {
    constructor(status, body) {
        super(`Widget API error ${status}`);
        this.name = 'ApiError';
        this.status = status;
        this.body = body;
    }
}

export class WidgetApi {
    /**
     * @param {string} publicKey
     * @param {string} baseUrl  например 'https://api.dokasearch.io' (без завершающего слеша)
     * @param {typeof fetch} fetchImpl
     * @param {string} [visitorId]  BL-D1 — стабильный per-browser UUID, отдаётся
     *                              в заголовке `X-Visitor-Id`, чтобы search-эндпоинт
     *                              мог питать сессионный HLL биллинга.
     *                              Пустая строка отключает заголовок (сервер
     *                              откатывается на ip+UA fingerprint).
     */
    constructor(publicKey, baseUrl, fetchImpl = globalThis.fetch.bind(globalThis), visitorId = '') {
        this.publicKey = publicKey;
        this.baseUrl = baseUrl.replace(/\/+$/, '');
        this.fetch = fetchImpl;
        this.visitorId = visitorId;
    }

    /**
     * BL-H1 — запрашивает widget config (brand_badge и т.д.) один раз при
     * первом mount. Возвращает `{brand_badge: 'mandatory'|'optional'|'hidden'}`.
     * Вызывающий код ловит сетевые сбои и откатывается на `'mandatory'`,
     * чтобы атрибуция побеждала на любом пути с ошибкой.
     *
     * @param {{signal?: AbortSignal}} [opts]
     * @returns {Promise<{brand_badge: 'mandatory'|'optional'|'hidden'}>}
     */
    async widgetConfig(opts = {}) {
        const url = `${this.baseUrl}/api/v1/widget/config`;
        return this._json(url, { method: 'GET' }, opts.signal);
    }

    /**
     * @param {string} query
     * @param {{
     *     limit?: number,
     *     signal?: AbortSignal,
     *     offset?: number,
     *     filters?: Record<string, string|number|Array<string|number>|{min?: number, max?: number}>
     * }} [opts]
     * @returns {Promise<SearchResponse>}
     */
    async search(query, opts = {}) {
        const limit = opts.limit ?? 12;
        const params = new URLSearchParams();
        params.set('q', query);
        params.set('limit', String(limit));
        if (typeof opts.offset === 'number' && opts.offset > 0) {
            params.set('offset', String(opts.offset));
        }
        if (opts.filters) {
            for (const [field, value] of Object.entries(opts.filters)) {
                if (value === undefined || value === null) continue;
                if (Array.isArray(value)) {
                    for (const v of value) {
                        params.append(`filter[${field}][]`, String(v));
                    }
                } else if (typeof value === 'object') {
                    if (value.min !== undefined && value.min !== null) {
                        params.set(`filter[${field}][min]`, String(value.min));
                    }
                    if (value.max !== undefined && value.max !== null) {
                        params.set(`filter[${field}][max]`, String(value.max));
                    }
                } else {
                    params.set(`filter[${field}]`, String(value));
                }
            }
        }
        const url = `${this.baseUrl}/api/v1/search?${params.toString()}`;
        return this._json(url, { method: 'GET' }, opts.signal);
    }

    /**
     * @param {string} query
     * @param {{limit?: number, signal?: AbortSignal}} [opts]
     * @returns {Promise<SuggestResponse>}
     */
    async suggest(query, opts = {}) {
        const limit = opts.limit ?? 8;
        const url = `${this.baseUrl}/api/v1/suggest?q=${encodeURIComponent(query)}&limit=${limit}`;
        return this._json(url, { method: 'GET' }, opts.signal);
    }

    /**
     * @param {{queryId: string, productId: string, position: number, sessionId?: string}} payload
     * @returns {Promise<void>}
     */
    async trackClick({ queryId, productId, position, sessionId }) {
        const url = `${this.baseUrl}/api/v1/analytics/click`;
        const body = JSON.stringify({
            query_id: queryId,
            product_id: productId,
            position,
            ...(sessionId ? { session_id: sessionId } : {}),
        });

        // POST клика использует 1-секундный timeout, чтобы нестабильный
        // analytics-эндпоинт заметно не задерживал переход к товару. Виджет ловит
        // timeout и в любом случае продолжает навигацию.
        const timeoutSignal = AbortSignal.timeout
            ? AbortSignal.timeout(1000)
            : (() => { const c = new AbortController(); setTimeout(() => c.abort(), 1000); return c.signal; })();

        const res = await this.fetch(url, {
            method: 'POST',
            headers: this._headers({ 'Content-Type': 'application/json' }),
            body,
            signal: timeoutSignal,
        });
        if (!res.ok) {
            throw new ApiError(res.status, await this._safeText(res));
        }
    }

    async _json(url, init, signal) {
        const res = await this.fetch(url, {
            ...init,
            headers: this._headers(),
            signal,
        });
        if (!res.ok) {
            throw new ApiError(res.status, await this._safeText(res));
        }
        return res.json();
    }

    _headers(extra = {}) {
        const base = {
            'Accept': 'application/json',
            'X-Public-Key': this.publicKey,
        };
        if (this.visitorId) {
            base['X-Visitor-Id'] = this.visitorId;
        }
        return { ...base, ...extra };
    }

    async _safeText(res) {
        try { return await res.text(); } catch { return ''; }
    }
}
